import React, { useState, useRef, useEffect } from 'react';
import styles from './TheologyPage.module.css';

const API_BASE = process.env.REACT_APP_API_BASE || `http://${window.location.hostname}:3001`;

// ── Whisper model options ──────────────────────────────────────────────────
const WHISPER_MODELS = [
  { value: 'tiny', label: 'Tiny  (最快，精度低)' },
  { value: 'base', label: 'Base  (快)' },
  { value: 'small', label: 'Small (平衡)' },
  { value: 'medium', label: 'Medium (推荐中文)' },
  { value: 'large-v2', label: 'Large-v2' },
  { value: 'large-v3', label: 'Large-v3  (最准，推荐讲道)' },
];

const SERMON_STYLES = [
  { value: 'auto', label: 'AI 自动判断（推荐）' },
  { value: 'three_point', label: '三点式 / Three-Point' },
  { value: 'thematic', label: '主题式 / Thematic' },
  { value: 'expository', label: '释经式 / Expository' },
];

// ── Log colour tags ────────────────────────────────────────────────────────
const TAG_COLOURS = {
  info: '#9cdcfe',
  success: '#4ec9b0',
  warning: '#ce9178',
  error: '#f44747',
};

export default function TheologyPage() {
  const [showTool, setShowTool] = useState(false);
  const [panel, setPanel] = useState('input'); // input | running | done
  const [formData, setFormData] = useState({
    youtubeUrl: '',
    geminiKey: localStorage.getItem('sermon_gemini_key') || '',
    whisperModel: localStorage.getItem('sermon_whisper_model') || 'medium',
    sermonStyle: 'auto',
  });
  const [taskId, setTaskId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [logs, setLogs] = useState([]);
  const [outline, setOutline] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('outline'); // outline | json
  const logEndRef = useRef(null);
  const esRef = useRef(null);

  // Auto-scroll log
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  // Persist settings
  useEffect(() => {
    localStorage.setItem('sermon_gemini_key', formData.geminiKey);
    localStorage.setItem('sermon_whisper_model', formData.whisperModel);
  }, [formData.geminiKey, formData.whisperModel]);

  const field = (key) => ({
    value: formData[key],
    onChange: (e) => setFormData(f => ({ ...f, [key]: e.target.value })),
  });

  // ── Start processing ────────────────────────────────────────────────────
  const handleStart = async () => {
    const url = formData.youtubeUrl.trim();
    if (!url) { setError('请输入 YouTube 链接'); return; }
    if (!'youtube.com youtu.be'.split(' ').some(d => url.includes(d))) {
      setError('请输入有效的 YouTube 链接'); return;
    }
    if (!formData.geminiKey.trim()) { setError('请输入 Gemini API Key'); return; }

    setError('');
    setLogs([]);
    setProgress(0);
    setOutline(null);
    setPanel('running');

    try {
      const res = await fetch(`${API_BASE}/api/sermon/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          youtube_url: formData.youtubeUrl.trim(),
          gemini_api_key: formData.geminiKey.trim(),
          whisper_model: formData.whisperModel,
          sermon_style: formData.sermonStyle,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || '启动失败');
      }
      const { task_id } = await res.json();
      setTaskId(task_id);
      startSSE(task_id);
    } catch (err) {
      setError(err.message);
      setPanel('input');
    }
  };

  // ── SSE progress listener ───────────────────────────────────────────────
  const startSSE = (id) => {
    if (esRef.current) esRef.current.close();
    const es = new EventSource(`${API_BASE}/api/sermon/progress/${id}`);
    esRef.current = es;

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        setProgress(data.progress ?? 0);
        setMessage(data.message ?? '');
        if (data.new_logs?.length) {
          setLogs(prev => [...prev, ...data.new_logs]);
        }
        if (data.status === 'done') {
          es.close();
          fetchResult(id);
        }
        if (data.status === 'error') {
          es.close();
          setError(data.message || '处理出错');
          setPanel('input');
        }
      } catch (_) { }
    };

    es.onerror = () => {
      es.close();
      // SSE closed after completion is normal; check if we have a result
      if (panel !== 'done') {
        setTimeout(() => fetchResult(id), 500);
      }
    };
  };

  const fetchResult = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/sermon/result/${id}`);
      if (!res.ok) return;
      const data = await res.json();
      setOutline(data.outline);
      setPanel('done');
      setProgress(100);
    } catch (_) { }
  };

  const handleDownload = async () => {
    if (!taskId) return;
    window.open(`${API_BASE}/api/sermon/download/${taskId}`, '_blank');
  };

  const handleReset = () => {
    if (esRef.current) esRef.current.close();
    setPanel('input');
    setTaskId(null);
    setLogs([]);
    setProgress(0);
    setOutline(null);
    setError('');
  };

  return (
    <div className={styles.page}>
      {/* ── Hero header ─────────────────────────────────────────── */}
      <div className={styles.hero}>
        <div className={styles.heroIcon}>✝</div>
        <h1 className={styles.heroTitle}>Theology</h1>
        <p className={styles.heroDesc}>
          神学研究与讲道资源 · Theological Studies &amp; Sermon Resources
        </p>
      </div>

      {/* ── Resource cards ──────────────────────────────────────── */}
      <div className={styles.resourceGrid}>
        <div className={styles.resourceCard}>
          <span className={styles.rcIcon}>📖</span>
          <h3>圣经研读</h3>
          <p>系统性圣经阅读计划与注释</p>
        </div>
        <div className={styles.resourceCard}>
          <span className={styles.rcIcon}>🙏</span>
          <h3>祷告资源</h3>
          <p>每日灵修与祷告引导</p>
        </div>

        {/* ── Sermon Tool card ─────────────────────────────────── */}
        <div
          className={`${styles.resourceCard} ${styles.resourceCardHighlight}`}
          onClick={() => setShowTool(true)}
          id="sermon-tool-card"
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && setShowTool(true)}
          aria-label="打开讲道工具"
        >
          <span className={styles.rcIcon}>🎙</span>
          <h3>Sermon Tool</h3>
          <p>YouTube 讲道 → AI 讲道总结 → Word 文档</p>
          <div className={styles.launchBtn}>
            <span>启动工具</span>
            <span className={styles.launchArrow}>→</span>
          </div>
        </div>

        <div className={styles.resourceCard}>
          <span className={styles.rcIcon}>📚</span>
          <h3>神学文献</h3>
          <p>经典神学著作与参考资料</p>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════
          Sermon Tool Panel (modal overlay)
      ══════════════════════════════════════════════════════════ */}
      {showTool && (
        <div className={styles.overlay} onClick={e => e.target === e.currentTarget && panel !== 'running' && handleReset() && setShowTool(false)}>
          <div className={styles.modal} role="dialog" aria-label="讲道总结工具">

            {/* Modal header */}
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>
                <span className={styles.modalTitleIcon}>🎙</span>
                <div>
                  <h2>讲道总结生成工具</h2>
                  <p>Sermon Summary Generator</p>
                </div>
              </div>
              <button
                className={styles.closeBtn}
                onClick={() => { handleReset(); setShowTool(false); }}
                aria-label="关闭"
                disabled={panel === 'running'}
              >✕</button>
            </div>

            {/* ── INPUT PANEL ──────────────────────────────────── */}
            {panel === 'input' && (
              <div className={styles.inputPanel}>
                {error && <div className={styles.errorBanner}>{error}</div>}

                {/* URL */}
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    <span className={styles.stepBadge}>①</span>
                    YouTube 讲道链接
                  </label>
                  <div className={styles.urlRow}>
                    <input
                      id="sermon-youtube-url"
                      type="url"
                      className={styles.input}
                      placeholder="https://www.youtube.com/watch?v=..."
                      {...field('youtubeUrl')}
                    />
                    <button
                      className={styles.pasteBtn}
                      onClick={async () => {
                        try {
                          const text = await navigator.clipboard.readText();
                          setFormData(f => ({ ...f, youtubeUrl: text }));
                        } catch (_) { }
                      }}
                    >粘贴</button>
                  </div>
                </div>

                {/* Gemini Key */}
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    <span className={styles.stepBadge}>②</span>
                    Gemini API Key
                    <span className={styles.savedBadge}>自动保存</span>
                  </label>
                  <input
                    id="sermon-gemini-key"
                    type="password"
                    className={styles.input}
                    placeholder="AIza..."
                    {...field('geminiKey')}
                  />
                </div>

                {/* Options row */}
                <div className={styles.optionsRow}>
                  <div className={styles.fieldGroup} style={{ flex: 1 }}>
                    <label className={styles.fieldLabel}>
                      <span className={styles.stepBadge}>③</span>
                      Whisper 模型
                    </label>
                    <select
                      id="sermon-whisper-model"
                      className={styles.select}
                      value={formData.whisperModel}
                      onChange={e => setFormData(f => ({ ...f, whisperModel: e.target.value }))}
                    >
                      {WHISPER_MODELS.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.fieldGroup} style={{ flex: 1 }}>
                    <label className={styles.fieldLabel}>
                      <span className={styles.stepBadge}>④</span>
                      讲道结构风格
                    </label>
                    <select
                      id="sermon-style"
                      className={styles.select}
                      value={formData.sermonStyle}
                      onChange={e => setFormData(f => ({ ...f, sermonStyle: e.target.value }))}
                    >
                      {SERMON_STYLES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Tips */}
                <div className={styles.tipBox}>
                  <span className={styles.tipIcon}>💡</span>
                  <div>
                    <strong>使用提示：</strong> 首次使用 large-v3 模型将自动下载约 1.5GB 模型文件。
                    处理一段 60 分钟讲道大约需要 5–15 分钟，请耐心等待。
                    Gemini API Key 会保存在本地浏览器中。
                  </div>
                </div>

                <button
                  id="sermon-start-btn"
                  className={styles.startBtn}
                  onClick={handleStart}
                >
                  ▶ 开始生成讲道总结  ·  Generate Sermon Summary
                </button>
              </div>
            )}

            {/* ── RUNNING PANEL ─────────────────────────────────── */}
            {panel === 'running' && (
              <div className={styles.runningPanel}>
                {/* Progress bar */}
                <div className={styles.progressSection}>
                  <div className={styles.progressHeader}>
                    <span className={styles.progressLabel}>{message}</span>
                    <span className={styles.progressPct}>{Math.round(progress)}%</span>
                  </div>
                  <div className={styles.progressTrack}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>

                {/* Log console */}
                <div className={styles.logConsole}>
                  <div className={styles.logHeader}>
                    <span className={styles.logDot} />
                    <span className={styles.logDot} style={{ background: '#f5a623' }} />
                    <span className={styles.logDot} style={{ background: '#7ed321' }} />
                    <span style={{ marginLeft: 8, fontSize: '0.78rem', color: '#888' }}>
                      处理日志  ·  Process Log
                    </span>
                  </div>
                  <div className={styles.logBody} id="sermon-log-body">
                    {logs.map((log, i) => (
                      <div key={i} className={styles.logLine}>
                        <span className={styles.logTs}>[{log.ts}]</span>
                        <span style={{ color: TAG_COLOURS[log.tag] || TAG_COLOURS.info }}>
                          {log.msg}
                        </span>
                      </div>
                    ))}
                    <div ref={logEndRef} />
                  </div>
                </div>

                <div className={styles.processingNote}>
                  <div className={styles.spinner} />
                  处理中，请勿关闭此窗口…
                </div>
              </div>
            )}

            {/* ── DONE PANEL ────────────────────────────────────── */}
            {panel === 'done' && outline && (
              <div className={styles.donePanel}>
                {/* Success header */}
                <div className={styles.successHeader}>
                  <div className={styles.successIcon}>✅</div>
                  <div>
                    <h3 className={styles.successTitle}>{outline.title || '讲道大纲已生成'}</h3>
                    <p className={styles.successSub}>
                      {outline.scripture && <><em>📖 {outline.scripture}</em> · </>}
                      {outline.style}
                    </p>
                    {outline.summary && (
                      <p className={styles.summaryTag}>「{outline.summary}」</p>
                    )}
                  </div>
                </div>

                {/* Tab bar */}
                <div className={styles.tabBar}>
                  <button
                    id="sermon-tab-outline"
                    className={`${styles.tab} ${activeTab === 'outline' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('outline')}
                  >讲道大纲</button>
                  <button
                    id="sermon-tab-json"
                    className={`${styles.tab} ${activeTab === 'json' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('json')}
                  >原始 JSON</button>
                </div>

                {/* Tab content */}
                <div className={styles.tabContent}>
                  {activeTab === 'outline' && (
                    <OutlineView outline={outline} />
                  )}
                  {activeTab === 'json' && (
                    <pre className={styles.jsonPre}>
                      {JSON.stringify(outline, null, 2)}
                    </pre>
                  )}
                </div>

                {/* Action buttons */}
                <div className={styles.actionRow}>
                  <button
                    id="sermon-download-btn"
                    className={styles.downloadBtn}
                    onClick={handleDownload}
                  >
                    📄 下载 Word 文档  ·  Download .docx
                  </button>
                  <button
                    id="sermon-again-btn"
                    className={styles.againBtn}
                    onClick={handleReset}
                  >
                    🔄 处理新讲道
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Outline Renderer
// ══════════════════════════════════════════════════════════════════════════════

function OutlineView({ outline }) {
  return (
    <div className={styles.outlineView}>
      {/* Introduction */}
      {outline.introduction && (
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>
            <span className={styles.sectionBar} />
            {outline.introduction.heading || '引言'}
          </h4>
          <p className={styles.sectionContent}>{outline.introduction.content}</p>
          {outline.introduction.key_quotes?.map((q, i) => (
            <blockquote key={i} className={styles.quoteBlock}>❝ {q}</blockquote>
          ))}
        </section>
      )}

      {/* Main points */}
      {outline.main_points?.map((pt, pi) => (
        <section key={pi} className={styles.section}>
          <h4 className={styles.sectionTitle}>
            <span className={styles.sectionBar} style={{ background: '#1976d2' }} />
            第{CN[pi] || pt.number}点：{pt.heading}
          </h4>
          {pt.scripture_reference && (
            <p className={styles.scriptureRef}>📖 {pt.scripture_reference}</p>
          )}

          {/* Sub-points */}
          {pt.subtitles?.map((sub, si) => (
            <div key={si} className={styles.subPoint}>
              <h5 className={styles.subTitle}>▸ {sub.number}. {sub.heading}</h5>
              {sub.scripture_ref && (
                <p className={styles.scriptureRef} style={{ marginLeft: 16 }}>📖 {sub.scripture_ref}</p>
              )}
              <ul className={styles.bulletList}>
                {sub.bullets?.map((b, bi) => <li key={bi}>{b}</li>)}
              </ul>
              {sub.detail && <p className={styles.detail}>{sub.detail}</p>}
            </div>
          ))}

          {pt.content && <p className={styles.sectionContent}>{pt.content}</p>}

          {pt.key_quotes?.length > 0 && (
            <div className={styles.quotesGroup}>
              <h6 className={styles.quotesLabel}>重点话语</h6>
              {pt.key_quotes.map((q, qi) => (
                <blockquote key={qi} className={styles.quoteBlock}>❝ {q}</blockquote>
              ))}
            </div>
          )}
          {pt.application && (
            <div className={styles.applicationBox}>
              ✦ <strong>应用建议：</strong> {pt.application}
            </div>
          )}
        </section>
      ))}

      {/* Conclusion */}
      {outline.conclusion && (
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>
            <span className={styles.sectionBar} style={{ background: '#8b4513' }} />
            {outline.conclusion.heading || '结语'}
          </h4>
          <p className={styles.sectionContent}>{outline.conclusion.content}</p>
          {outline.conclusion.bullets?.length > 0 && (
            <ul className={styles.bulletList}>
              {outline.conclusion.bullets.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          )}
          {outline.conclusion.key_quotes?.map((q, i) => (
            <blockquote key={i} className={styles.quoteBlock}>❝ {q}</blockquote>
          ))}
        </section>
      )}
    </div>
  );
}

const CN = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
